import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

const PANEL_BG = '#222d3f';
const ACCENT = '#5eead4';

function monthToNum(ym: string): number {
  const [y, m] = ym.split('-').map((v) => Number.parseInt(v, 10));
  return y * 12 + m;
}

function parseMonthValue(month: string): { year: number; mon: number } {
  const [y, m] = month.split('-').map((v) => Number.parseInt(v, 10));
  const now = new Date();
  return {
    year: Number.isFinite(y) ? y : now.getFullYear(),
    mon: Number.isFinite(m) ? m : now.getMonth() + 1,
  };
}

function formatMonth(year: number, mon: number): string {
  return `${year}-${String(mon).padStart(2, '0')}`;
}

function listMonthsBetween(minMonth: string, maxMonth: string): string[] {
  const out: string[] = [];
  let { year, mon } = parseMonthValue(minMonth);
  const end = monthToNum(maxMonth);
  while (monthToNum(formatMonth(year, mon)) <= end) {
    out.push(formatMonth(year, mon));
    mon += 1;
    if (mon > 12) {
      mon = 1;
      year += 1;
    }
  }
  return out;
}

export type MonthPickerSheetProps = {
  open: boolean;
  value: string;
  onClose: () => void;
  onConfirm: (month: string) => void;
  minYear?: number;
  maxYear?: number;
  /** YYYY-MM — earliest selectable month (inclusive). */
  minMonth?: string;
  /** YYYY-MM — latest selectable month (inclusive). */
  maxMonth?: string;
};

export function MonthPickerSheet({
  open,
  value,
  onClose,
  onConfirm,
  minYear = 2020,
  maxYear = new Date().getFullYear(),
  minMonth,
  maxMonth,
}: MonthPickerSheetProps) {
  const { t } = useTranslation();
  const parsed = parseMonthValue(value);
  const [year, setYear] = useState(parsed.year);
  const [mon, setMon] = useState(parsed.mon);

  useEffect(() => {
    if (!open) return;
    const next = parseMonthValue(value);
    setYear(next.year);
    setMon(next.mon);
  }, [open, value]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const minNum = minMonth ? monthToNum(minMonth) : null;
  const maxNum = maxMonth ? monthToNum(maxMonth) : null;

  const monthOptions = useMemo(() => {
    if (minMonth && maxMonth) {
      return listMonthsBetween(minMonth, maxMonth).reverse();
    }
    return [];
  }, [minMonth, maxMonth]);

  const useFlatList = monthOptions.length > 0 && monthOptions.length <= 24;

  const years = useMemo(() => {
    const list: number[] = [];
    for (let y = maxYear; y >= minYear; y -= 1) {
      const yearMin = y * 12 + 1;
      const yearMax = y * 12 + 12;
      if (minNum !== null && yearMax < minNum) continue;
      if (maxNum !== null && yearMin > maxNum) continue;
      list.push(y);
    }
    return list;
  }, [minYear, maxYear, minNum, maxNum]);

  const availableMonths = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => i + 1).filter((m) => {
      const num = year * 12 + m;
      if (minNum !== null && num < minNum) return false;
      if (maxNum !== null && num > maxNum) return false;
      return true;
    });
  }, [year, minNum, maxNum]);

  useEffect(() => {
    if (!open || useFlatList || availableMonths.length === 0) return;
    if (!availableMonths.includes(mon)) {
      setMon(availableMonths[availableMonths.length - 1]!);
    }
  }, [open, year, availableMonths, mon, useFlatList]);

  if (!open || typeof document === 'undefined') return null;

  function pickMonth(next: string) {
    onConfirm(next);
    onClose();
  }

  const sheet = (
    <div className="fixed inset-0 z-[120] flex flex-col justify-end">
      <button
        type="button"
        className="absolute inset-0 bg-black/55 touch-manipulation"
        aria-label={t('common.close')}
        onClick={onClose}
      />
      <div
        className="relative rounded-t-2xl pb-[max(1rem,env(safe-area-inset-bottom))]"
        style={{ background: PANEL_BG }}
      >
        <div className="flex items-center justify-between border-b px-4 py-3" style={{ borderColor: '#131f2f' }}>
          <button type="button" className="touch-manipulation py-1 text-sm text-white/70" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <span className="text-sm font-medium text-white">{t('incomeDetails.pickMonth', '选择月份')}</span>
          {!useFlatList ? (
            <button
              type="button"
              className="touch-manipulation py-1 text-sm font-medium"
              style={{ color: ACCENT }}
              onClick={() => pickMonth(formatMonth(year, mon))}
            >
              {t('common.confirm')}
            </button>
          ) : (
            <span className="w-10" aria-hidden />
          )}
        </div>

        {useFlatList ? (
          <div className="flex flex-col gap-2 px-4 py-4">
            {monthOptions.map((ym) => {
              const active = ym === value;
              return (
                <button
                  key={ym}
                  type="button"
                  onClick={() => pickMonth(ym)}
                  className="touch-manipulation flex h-12 w-full items-center justify-center rounded-xl text-base font-semibold transition active:scale-[0.98]"
                  style={{
                    background: active ? 'rgba(94, 234, 212, 0.18)' : '#131f2f',
                    color: active ? ACCENT : '#fff',
                    border: active ? `1px solid ${ACCENT}55` : '1px solid transparent',
                  }}
                >
                  {ym}
                </button>
              );
            })}
          </div>
        ) : (
          <div className="px-4 py-4">
            <div className="mb-3 flex flex-wrap justify-center gap-2">
              {years.map((y) => (
                <button
                  key={y}
                  type="button"
                  onClick={() => setYear(y)}
                  className="touch-manipulation rounded-lg px-3 py-2 text-sm font-semibold transition active:scale-[0.98]"
                  style={{
                    background: y === year ? 'rgba(94, 234, 212, 0.18)' : '#131f2f',
                    color: y === year ? ACCENT : '#fff',
                  }}
                >
                  {y}
                  {t('incomeDetails.yearSuffix')}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-4 gap-2">
              {availableMonths.map((m) => {
                const active = m === mon;
                return (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setMon(m)}
                    className="touch-manipulation flex h-11 items-center justify-center rounded-xl text-sm font-semibold transition active:scale-[0.98]"
                    style={{
                      background: active ? 'rgba(94, 234, 212, 0.18)' : '#131f2f',
                      color: active ? ACCENT : '#fff',
                    }}
                  >
                    {String(m).padStart(2, '0')}
                    {t('incomeDetails.monthSuffix')}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(sheet, document.body);
}
